import {
  useEffect,
  useRef,
  useState,
  forwardRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Sparkles } from "lucide-react";
import { trpcClient } from "@/trpc/client";
import type { CompanyIdentity } from "@/types/businessData";
import CompanyProfileStep, { type StepHandle } from "./CompanyProfileStep";

interface BusinessProfileStepProps {
  businessId: string | null;
  /** Full URL as saved at create-business (may be empty). */
  website: string | undefined;
  initialData?: CompanyIdentity;
  errorMsg: string | null;
}

type FetchState = "idle" | "fetching" | "drafted" | "manual";

/**
 * AI-native company profile step: when the business has a website and no
 * human-entered profile yet, the server fetches the site and drafts the
 * profile — the user reviews and edits instead of typing from scratch.
 * Falls back silently to the empty manual form.
 */
const BusinessProfileStep = forwardRef<StepHandle, BusinessProfileStepProps>(
  function BusinessProfileStep(
    { businessId, website, initialData, errorMsg },
    ref,
  ) {
    const { t } = useTranslation("onboarding");
    const hasExistingProfile = Boolean(initialData?.description?.trim());
    const canFetch = Boolean(businessId && website && !hasExistingProfile);

    const [state, setState] = useState<FetchState>(
      canFetch ? "fetching" : "manual",
    );
    const [draft, setDraft] = useState<CompanyIdentity | undefined>(undefined);
    const startedRef = useRef(false);

    useEffect(() => {
      if (!canFetch || startedRef.current) return;
      startedRef.current = true;
      let cancelled = false;

      trpcClient.onboarding.fetchBusinessInfo
        .mutate({ businessId: businessId!, website: website! })
        .then((result) => {
          if (cancelled) return;
          const hasContent =
            result.description.trim() !== "" ||
            result.culture.trim() !== "" ||
            result.industry.trim() !== "" ||
            result.businessModel.length > 0;
          if (hasContent) {
            setDraft({
              description: result.description,
              culture: result.culture,
              businessModel: result.businessModel,
              industry: result.industry,
            });
            setState("drafted");
          } else {
            setState("manual");
          }
        })
        .catch(() => {
          if (!cancelled) setState("manual");
        });

      return () => {
        cancelled = true;
      };
    }, [canFetch, businessId, website]);

    if (state === "fetching") {
      return (
        <div className="space-y-5 py-6">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-5 w-5 animate-pulse text-primary" />
          </div>
          <div className="space-y-1.5">
            <p className="font-medium text-foreground">
              {t("businessProfile.fetchingTitle")}
            </p>
            <p className="max-w-md text-[15px] leading-relaxed text-muted-foreground">
              {t("businessProfile.fetchingHint")}
            </p>
          </div>
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {state === "drafted" && (
          <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <p className="text-muted-foreground">
              {t("businessProfile.draftedNotice")}
            </p>
          </div>
        )}
        <CompanyProfileStep
          ref={ref}
          initialData={state === "drafted" ? draft : initialData}
          errorMsg={errorMsg}
        />
      </div>
    );
  },
);

export default BusinessProfileStep;
